import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Drawer } from "@/components/ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pencil, Trash, Plus, Search, X, Settings } from "lucide-react";
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { getApiUrl } from "@/utils/api";
import { toast } from "react-toastify";
import { DeleteModal } from "@/components/delete-modal";
import { useTranslation } from "react-i18next";
import { Pagination } from "@/components/pagination";
import { FuelTypeForm } from "./FuelTypesForm";
import { PageHeader } from "@/components/PageHeader";

interface Category {
  id: number;
  name: string;
}

interface FuelType {
  id: number;
  name: string;
  category: Category;
  created_at: string;
  updated_at: string;
}

interface ApiResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const FuelTypesList = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("none");

  const PAGE_SIZE = 10;

  // Fetch categories for filter
  const { data: categories } = useQuery<Category[]>({
    queryKey: ["fuel-type-categories"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("categories/"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      const data = await response.json();
      return data.results;
    },
    enabled: !!token,
  });

  // Fetch fuel types
  const { data, isLoading, error, refetch } = useQuery<ApiResponse<FuelType>>({
    queryKey: [
      "fuel-types",
      currentPage,
      isSearchActive ? searchQuery : undefined,
      selectedCategory !== "none" ? selectedCategory : undefined,
    ],
    queryFn: async () => {
      const offset = (currentPage - 1) * PAGE_SIZE;
      let url = `fuel-types/?limit=${PAGE_SIZE}&offset=${offset}`;

      if (isSearchActive && searchQuery) {
        url += `&name=${encodeURIComponent(searchQuery)}`;
      }

      if (selectedCategory !== "none") {
        url += `&category=${encodeURIComponent(selectedCategory)}`;
      }

      const response = await fetch(getApiUrl(url), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(t("fuelTypes.error.fetch"));
      }
      return response.json();
    },
    enabled: !!token,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(getApiUrl(`fuel-types/${id}/`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || t("fuelTypes.error.delete"));
      }
      return id; // Return the deleted ID for use in onSuccess
    },
    onSuccess: () => {
      // Invalidate and refetch the list
      queryClient.invalidateQueries({ queryKey: ["fuel-types"] });

      // Show success message
      toast.success(t("fuelTypes.deleteSuccess"));

      // Only close the modal after successful deletion
      setItemToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || t("fuelTypes.error.delete"));
    },
  });

  const handleAdd = () => {
    setEditingId(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (id: number) => {
    setEditingId(id);
    setIsDrawerOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setIsSearchActive(true);
      setCurrentPage(1);
    }
  };

  const handleResetSearch = () => {
    setSearchQuery("");
    setIsSearchActive(false);
    setCurrentPage(1);
  };

  // Reset search when search query becomes empty
  useEffect(() => {
    if (!searchQuery.trim()) {
      setIsSearchActive(false);
    }
  }, [searchQuery]);

  const handleDelete = () => {
    if (itemToDelete) {
      deleteMutation.mutate(itemToDelete);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500">
        {t("fuelTypes.error.fetch")}: {error.message}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full pb-8">
      <PageHeader
        icon={<Settings className="w-12 h-12 text-primary-foreground" />}
        actionTitle="fuelTypes.add"
        title="fuelTypes.title"
        description="fuelTypes.description"
        handleNew={handleAdd}
      />

      <div className="flex items-center space-x-2 p-4">
        <div className="relative w-[300px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <Input
            className="pl-10"
            placeholder={t("common.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setIsSearchActive(true);
                setCurrentPage(1);
              }
            }}
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full"
              onClick={() => {
                setSearchQuery("");
                setIsSearchActive(false);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className="w-[200px]">
          <Select
            value={selectedCategory}
            onValueChange={(value) => {
              setSelectedCategory(value);
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("common.selectCategory")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("common.allCategories")}</SelectItem>
              {categories?.map((category) => (
                <SelectItem key={category.id} value={category.name}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="px-4">
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("fuelTypes.table.id")}</TableHead>
                <TableHead>{t("fuelTypes.table.name")}</TableHead>
                <TableHead>{t("fuelTypes.table.unit")}</TableHead>
                <TableHead>{t("fuelTypes.table.category")}</TableHead>
                <TableHead className="text-right">
                  {t("fuelTypes.table.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.results?.length ? (
                data.results.map((supplier) => (
                  <TableRow key={supplier.id}>
                    <TableCell variant="compact" className="font-medium">
                      {supplier.id}
                    </TableCell>
                    <TableCell variant="compact" className="font-medium">
                      {supplier.name}
                    </TableCell>
                    <TableCell
                      variant="compact"
                      className="font-medium text-center"
                    >
                      {supplier.unit}
                    </TableCell>
                    <TableCell
                      variant="compact"
                      className="font-medium text-center"
                    >
                      {supplier.category?.name || "-"}
                    </TableCell>
                    <TableCell
                      variant="compact"
                      className="text-right space-x-2"
                    >
                      <div className="flex justify-center items-center">
                        <div
                          role="button"
                          className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                          onClick={() => handleEdit(supplier.id)}
                        >
                          <Pencil
                            size={30}
                            fill="#000000"
                            stroke="#ffffff"
                            strokeWidth={1}
                          />
                        </div>
                        <div
                          role="button"
                          className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                          onClick={() => setItemToDelete(supplier.id)}
                        >
                          <Trash
                            size={30}
                            fill="#ff0000"
                            stroke="#ffffff"
                            strokeWidth={2}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {t("fuelTypes.noData")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {data && data.count > PAGE_SIZE && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(data.count / PAGE_SIZE)}
            hasNextPage={!!data.next}
            hasPreviousPage={!!data.previous}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <Drawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        direction="right"
      >
        <DrawerContent className="h-full w-[500px] ml-auto">
          <div className="max-h-[80vh] overflow-y-auto p-6">
            <DrawerHeader>
              <DrawerTitle>
                {editingId ? t("fuelTypes.editTitle") : t("fuelTypes.addTitle")}
              </DrawerTitle>
              <DrawerDescription>
                {editingId
                  ? t("fuelTypes.editDescription")
                  : t("fuelTypes.addDescription")}
              </DrawerDescription>
            </DrawerHeader>
            <div className="mt-4">
              <FuelTypeForm
                id={editingId}
                onSuccess={() => {
                  setIsDrawerOpen(false);
                  queryClient.invalidateQueries({ queryKey: ["fuel-types"] });
                }}
                onCancel={() => setIsDrawerOpen(false)}
              />
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <DeleteModal
        open={!!itemToDelete}
        onOpenChange={(open) => !open && setItemToDelete(null)}
        onDelete={handleDelete}
        title={t("fuelTypes.deleteTitle")}
        entity={t("fuelTypes.title")}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
};
